import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { B2bTrafficFine } from '../../../models/Contract';
import { ContractsService } from '../../../services/contracts.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { ErrorService } from '../../../shared/services/error.service';
import { TableAction } from '../../../shared/components/table/table.component';
import { B2bContractPlateOption } from '../b2b-contract-plate-select/b2b-contract-plate-select.component';
import { exportB2bTableToExcel } from '../../../utils/b2b-table-export.util';

@Component({
  selector: 'app-b2b-traffic-fines',
  templateUrl: './b2b-traffic-fines.component.html',
  styleUrls: ['../shared/b2b-operational-shared.css', './b2b-traffic-fines.component.css']
})
export class B2bTrafficFinesComponent implements OnInit {
  contractId: number | null = null;
  contractNumber = '';
  contractName = '';
  items: B2bTrafficFine[] = [];
  isLoading = false;
  submitting = false;
  showModal = false;
  viewMode = false;
  editingId: number | null = null;
  selectedVehicleId: number | null = null;
  /** Desconto do órgão. Não é o valor cobrado do cliente. */
  private preservedAdjustedAmount: number | null = null;
  form!: FormGroup;

  readonly paymentStatusOptions = [
    { value: 'A_Pagar', label: 'A pagar' },
    { value: 'Em_aberto', label: 'Em aberto' },
    { value: 'Pago_pela_empresa', label: 'Pago pela empresa' },
    { value: 'Pago_pelo_cliente', label: 'Pago pelo cliente' },
    { value: 'Reembolsado', label: 'Reembolsado' }
  ];

  readonly typeOptions = [
    { value: 'Multa', label: 'Multa' },
    { value: 'Notificação', label: 'Notificação' }
  ];

  columns = [
    { titulo: 'AIT', label: 'aipNumber' },
    { titulo: 'Placa', label: 'vehiclePlate' },
    { titulo: 'Emissão', label: 'issueDate', formatter: (v: any) => this.formatDate(v) },
    { titulo: 'Vencimento', label: 'dueDate', formatter: (v: any) => this.formatDate(v) },
    { titulo: 'Status', label: 'paymentStatus', formatter: (v: any) => this.paymentStatusLabel(v) },
    {
      titulo: 'Valor ao cliente',
      label: 'displayClientAmount',
      formatter: (v: any) => this.formatMoney(v),
      sensitive: true
    },
    {
      titulo: 'Valor original',
      label: 'originalAmount',
      formatter: (v: any) => this.formatMoney(v),
      sensitive: true
    },
    { titulo: 'Condutor', label: 'driverName' },
    { titulo: 'Local', label: 'infractionLocation' }
  ];

  actions: TableAction[] = [
    { label: 'Visualizar', icon: 'fas fa-eye', action: 'view', class: 'btn-view' },
    { label: 'Editar', icon: 'fas fa-edit', action: 'edit', class: 'btn-edit' },
    { label: 'Excluir', icon: 'fas fa-trash', action: 'delete', class: 'btn-delete' }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private contractsService: ContractsService,
    private errorService: ErrorService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      aipNumber: ['', Validators.required],
      vehiclePlate: ['', Validators.required],
      issueDate: ['', Validators.required],
      dueDate: ['', Validators.required],
      paidDate: [''],
      paymentStatus: ['A_Pagar', Validators.required],
      originalAmount: [null, [Validators.required, Validators.min(0.01)]],
      finalAmountToClient: [null],
      description: [''],
      driverName: [''],
      infractionDateTime: [''],
      infractionLocation: [''],
      city: [''],
      state: [''],
      type: ['Multa'],
      notes: ['']
    });

    this.route.params.subscribe(params => {
      this.contractId = +params['id'];
      if (this.contractId) {
        this.loadContractInfo();
        this.loadItems();
      }
    });
  }

  get modalTitle(): string {
    if (this.viewMode) return 'Visualizar multa';
    return this.editingId ? 'Editar multa' : 'Nova multa';
  }

  loadContractInfo(): void {
    if (!this.contractId) return;
    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (c) => {
        this.contractNumber = c.contractNumber || '';
        this.contractName = c.contractName || '';
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  loadItems(): void {
    if (!this.contractId) return;
    this.isLoading = true;
    this.contractsService.getB2bTrafficFines(this.contractId).subscribe({
      next: (list) => {
        this.items = (list || []).map(x => this.normalize(x));
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  openCreate(): void {
    this.viewMode = false;
    this.editingId = null;
    this.selectedVehicleId = null;
    this.preservedAdjustedAmount = null;
    this.form.enable();
    this.form.reset({
      aipNumber: '',
      vehiclePlate: '',
      issueDate: '',
      dueDate: '',
      paidDate: '',
      paymentStatus: 'A_Pagar',
      originalAmount: null,
      finalAmountToClient: null,
      description: '',
      driverName: '',
      infractionDateTime: '',
      infractionLocation: '',
      city: '',
      state: '',
      type: 'Multa',
      notes: ''
    });
    this.showModal = true;
  }

  openView(item: B2bTrafficFine): void {
    if (!this.contractId || !item?.id) {
      this.errorService.showErrorMessage('Não foi possível abrir o detalhe da multa.');
      return;
    }
    // Mesmo visualizar centralizado de Financeiro → Multas (abas Chat/Anexos/Chamado).
    this.router.navigate(['/b2b/contracts', this.contractId, 'multas', item.id, 'view'], {
      state: { listRow: item }
    });
  }

  openEdit(item: B2bTrafficFine): void {
    this.patchForm(item);
    this.viewMode = false;
    this.form.enable();
    this.showModal = true;
  }

  private patchForm(item: B2bTrafficFine): void {
    this.editingId = item.id ?? null;
    this.selectedVehicleId = item.vehicleId ?? null;
    this.preservedAdjustedAmount = this.organDiscount(item);
    this.form.patchValue({
      aipNumber: item.aipNumber || '',
      vehiclePlate: item.vehiclePlate || '',
      issueDate: this.toDateInput(item.issueDate),
      dueDate: this.toDateInput(item.dueDate),
      paidDate: this.toDateInput(item.paidDate),
      paymentStatus: item.paymentStatus || 'A_Pagar',
      originalAmount: item.originalAmount ?? null,
      finalAmountToClient: this.clientChargeForForm(item),
      description: item.description || '',
      driverName: item.driverName || '',
      infractionDateTime: this.toDateTimeInput(item.infractionDateTime),
      infractionLocation: item.infractionLocation || '',
      city: item.city || '',
      state: item.state || '',
      type: item.type || 'Multa',
      notes: item.notes || ''
    });
  }

  onPlateSelected(option: B2bContractPlateOption | null): void {
    this.selectedVehicleId = option?.vehicleId ?? null;
  }

  closeModal(): void {
    this.showModal = false;
    this.viewMode = false;
    this.editingId = null;
    this.selectedVehicleId = null;
    this.form.enable();
  }

  submit(): void {
    if (this.viewMode || !this.contractId || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (!this.selectedVehicleId) {
      this.errorService.showErrorMessage('Selecione uma placa válida do contrato.');
      return;
    }
    const raw = this.form.getRawValue();
    const originalAmount = Number(raw.originalAmount);
    const clientAmount =
      raw.finalAmountToClient != null && raw.finalAmountToClient !== ''
        ? Number(raw.finalAmountToClient)
        : originalAmount;
    const payload: B2bTrafficFine = {
      b2bContractId: this.contractId,
      aipNumber: raw.aipNumber,
      vehicleId: this.selectedVehicleId,
      vehiclePlate: raw.vehiclePlate || null,
      issueDate: raw.issueDate ? new Date(raw.issueDate).toISOString() : null,
      dueDate: raw.dueDate ? new Date(raw.dueDate).toISOString() : null,
      paidDate: raw.paidDate ? new Date(raw.paidDate).toISOString() : null,
      paymentStatus: raw.paymentStatus || 'A_Pagar',
      originalAmount,
      adjustedAmount: this.preservedAdjustedAmount,
      finalAmountToClient: clientAmount > 0 ? clientAmount : originalAmount,
      description: raw.description || null,
      driverName: raw.driverName || null,
      infractionDateTime: raw.infractionDateTime ? new Date(raw.infractionDateTime).toISOString() : null,
      infractionLocation: raw.infractionLocation || null,
      city: raw.city || null,
      state: raw.state || null,
      type: raw.type || null,
      notes: raw.notes || null
    };

    this.submitting = true;
    const req$ = this.editingId
      ? this.contractsService.updateB2bTrafficFine(this.contractId, this.editingId, { ...payload, id: this.editingId })
      : this.contractsService.createB2bTrafficFine(this.contractId, payload);

    req$.subscribe({
      next: () => {
        this.submitting = false;
        this.errorService.showSuccessMessage(this.editingId ? 'Multa atualizada.' : 'Multa criada.');
        this.closeModal();
        this.loadItems();
      },
      error: () => { this.submitting = false; }
    });
  }

  onActionClick(event: { action: string; item: B2bTrafficFine }): void {
    if (event.action === 'view') this.openView(event.item);
    if (event.action === 'edit') this.openEdit(event.item);
    if (event.action === 'delete') this.deleteItem(event.item);
  }

  deleteItem(item: B2bTrafficFine): void {
    if (!this.contractId || !item.id) return;
    this.confirmationService.confirmDelete(item.aipNumber || item.vehiclePlate || 'esta multa', 'a multa').subscribe({
      next: (ok) => {
        if (!ok) return;
        this.contractsService.deleteB2bTrafficFine(this.contractId!, item.id!).subscribe({
          next: () => {
            this.errorService.showSuccessMessage('Multa excluída.');
            this.loadItems();
          }
        });
      }
    });
  }


  exportExcel(): void {
    exportB2bTableToExcel(
      `b2b-multas-${this.contractNumber || this.contractId}`,
      this.columns,
      this.items,
      'Multas'
    );
  }

  navigateBack(): void {
    if (this.contractId) this.router.navigate(['/b2b/contracts', this.contractId, 'view']);
    else this.router.navigate(['/b2b/contracts']);
  }

  private normalize(dto: any): B2bTrafficFine & { displayClientAmount?: number | null } {
    const originalAmount = dto.originalAmount ?? dto.OriginalAmount ?? null;
    const adjustedAmount = dto.adjustedAmount ?? dto.AdjustedAmount ?? null;
    const finalAmountToClient = dto.finalAmountToClient ?? dto.FinalAmountToClient ?? null;
    return {
      id: dto.id ?? dto.Id,
      b2bContractId: dto.b2bContractId ?? dto.b2BContractId ?? dto.B2BContractId ?? this.contractId!,
      aipNumber: dto.aipNumber ?? dto.aipnumber ?? dto.AIPNumber ?? '',
      vehicleId: dto.vehicleId ?? dto.VehicleId ?? null,
      vehiclePlate: dto.vehiclePlate ?? dto.VehiclePlate ?? null,
      issueDate: dto.issueDate ?? dto.IssueDate ?? null,
      dueDate: dto.dueDate ?? dto.DueDate ?? null,
      paidDate: dto.paidDate ?? dto.PaidDate ?? null,
      paymentStatus: dto.paymentStatus ?? dto.PaymentStatus ?? 'A_Pagar',
      originalAmount,
      adjustedAmount,
      finalAmountToClient,
      displayClientAmount: this.displayedClientAmount(originalAmount, finalAmountToClient, adjustedAmount),
      amountPaid: dto.amountPaid ?? dto.AmountPaid ?? null,
      description: dto.description ?? dto.Description ?? null,
      driverName: dto.driverName ?? dto.DriverName ?? null,
      infractionDateTime: dto.infractionDateTime ?? dto.InfractionDateTime ?? null,
      infractionLocation: dto.infractionLocation ?? dto.InfractionLocation ?? null,
      city: dto.city ?? dto.City ?? null,
      state: dto.state ?? dto.State ?? null,
      type: dto.type ?? dto.Type ?? null,
      infractionStatus: dto.infractionStatus ?? dto.InfractionStatus ?? null,
      notes: dto.notes ?? dto.Notes ?? null
    };
  }

  private clientChargeForForm(item: B2bTrafficFine): number | null {
    return this.displayedClientAmount(item.originalAmount ?? null, item.finalAmountToClient ?? null, item.adjustedAmount ?? null);
  }

  private organDiscount(item: B2bTrafficFine): number | null {
    const original = Number(item.originalAmount) || 0;
    const adjusted = item.adjustedAmount;
    const final = item.finalAmountToClient;
    if (adjusted == null || Number(adjusted) <= 0) return null;
    if (final != null && Number(adjusted) >= original && Math.abs(Number(final) - Number(adjusted)) < 0.015) {
      return null;
    }
    return Number(adjusted);
  }

  private displayedClientAmount(
    originalAmount: number | null,
    finalAmountToClient: number | null,
    adjustedAmount: number | null
  ): number | null {
    const original = Number(originalAmount) || 0;
    const final = finalAmountToClient == null ? null : Number(finalAmountToClient);
    const adjusted = adjustedAmount == null ? null : Number(adjustedAmount);
    if (final != null && final > 0) {
      const copiedDiscount = adjusted != null && adjusted > 0 && adjusted < original && Math.abs(final - adjusted) < 0.015;
      if (!copiedDiscount) return final;
    }
    return original > 0 ? original : final;
  }

  private toDateInput(value: any): string {
    if (!value) return '';
    const d = new Date(value);
    return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  }

  private toDateTimeInput(value: any): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  formatDate(value: any): string {
    if (!value) return '—';
    try { return new Date(value).toLocaleDateString('pt-BR'); } catch { return String(value); }
  }

  formatMoney(value: any): string {
    if (value == null || value === '') return '—';
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  paymentStatusLabel(value: string): string {
    return this.paymentStatusOptions.find(s => s.value === value)?.label || value || '—';
  }
}
